import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Modal, Spinner, Table } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import { adminApi, extractError } from '../api/client';
import { useToast } from '../contexts/ToastContext';

const TYPES = [
  { value: 'federal', label: 'Federal' },
  { value: 'state', label: 'State' },
  { value: 'county', label: 'County' },
  { value: 'city', label: 'City' },
  { value: 'district', label: 'District' },
  { value: 'school_board', label: 'School Board' },
  { value: 'special_district', label: 'Special District' },
];

const EMPTY = { name: '', slug: '', type: 'city', state_id: '', county_id: '', parent_jurisdiction_id: '' };

export default function JurisdictionsPage() {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [states, setStates] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await adminApi.get('/admin/states?page_size=200');
        setStates(res.data.items || []);
      } catch (_e) {}
    })();
  }, []);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page_size: '500' });
      if (typeFilter) params.set('type', typeFilter);
      if (stateFilter) params.set('state_id', stateFilter);
      const res = await adminApi.get(`/admin/jurisdictions?${params.toString()}`);
      setItems(res.data.items || []);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [typeFilter, stateFilter]);

  function openCreate() {
    setEditing(null);
    setForm({ ...EMPTY, state_id: stateFilter || '' });
    setShowForm(true);
  }

  function openEdit(j) {
    setEditing(j);
    setForm({
      name: j.name || '', slug: j.slug || '', type: j.type || 'city',
      state_id: j.state_id || '', county_id: j.county_id || '',
      parent_jurisdiction_id: j.parent_jurisdiction_id || '',
    });
    setShowForm(true);
  }

  async function save() {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        slug: form.slug.trim() || form.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        type: form.type,
        state_id: form.state_id || null,
        county_id: form.county_id || null,
        parent_jurisdiction_id: form.parent_jurisdiction_id || null,
      };
      if (editing) {
        await adminApi.put(`/admin/jurisdictions/${editing.id}`, payload);
        toast.success('Jurisdiction updated');
      } else {
        await adminApi.post('/admin/jurisdictions', payload);
        toast.success('Jurisdiction created');
      }
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSaving(false);
    }
  }

  async function remove(j) {
    if (!window.confirm(`Delete jurisdiction "${j.name}"?`)) return;
    try {
      await adminApi.delete(`/admin/jurisdictions/${j.id}`);
      toast.success('Jurisdiction deleted');
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  }

  const groupedByType = useMemo(() => {
    const m = new Map();
    items.forEach((j) => {
      if (!m.has(j.type)) m.set(j.type, []);
      m.get(j.type).push(j);
    });
    return Array.from(m.entries()).sort();
  }, [items]);

  return (
    <>
      <PageHeader
        title="Jurisdictions"
        subtitle="Geographic/political scopes where Elections and Offices apply. States and counties are auto-seeded; add cities, districts, school boards as needed."
        actions={<Button variant="primary" onClick={openCreate} data-testid="jurisdictions-new-button"><i className="bi bi-plus-lg me-1" /> New jurisdiction</Button>}
      />
      <div className="page-content">
        {error && <Alert variant="danger">{error}</Alert>}
        <Card className="mb-3">
          <Card.Body>
            <div className="d-flex gap-2 flex-wrap">
              <div style={{ minWidth: 200 }}>
                <Form.Label className="text-muted" style={{ fontSize: '0.78rem' }}>Type</Form.Label>
                <Form.Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="jurisdictions-type-filter">
                  <option value="">All types</option>
                  {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </Form.Select>
              </div>
              <div style={{ minWidth: 220 }}>
                <Form.Label className="text-muted" style={{ fontSize: '0.78rem' }}>State</Form.Label>
                <Form.Select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} data-testid="jurisdictions-state-filter">
                  <option value="">All states</option>
                  {states.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </Form.Select>
              </div>
            </div>
          </Card.Body>
        </Card>

        {loading ? (
          <div className="text-center p-5"><Spinner animation="border" /></div>
        ) : items.length === 0 ? (
          <div className="empty-state"><i className="bi bi-geo" /><div>No jurisdictions match.</div></div>
        ) : (
          groupedByType.map(([type, rows]) => (
            <Card key={type} className="mb-3">
              <Card.Header><Badge bg="secondary" className="me-2">{type}</Badge>{rows.length} item{rows.length === 1 ? '' : 's'}</Card.Header>
              <Table size="sm" responsive hover className="mb-0">
                <thead><tr><th>Name</th><th>Slug</th><th>Scope</th><th className="text-end">Actions</th></tr></thead>
                <tbody>
                  {rows.map((j) => (
                    <tr key={j.id} data-testid={`jurisdiction-row-${j.id}`}>
                      <td className="fw-semibold">{j.name}</td>
                      <td><code>{j.slug}</code></td>
                      <td className="text-muted" style={{ fontSize: '0.85rem' }}>
                        {j.state_id ? `state:${j.state_id.slice(0,8)}…` : ''}
                        {j.county_id ? ` · county:${j.county_id.slice(0,8)}…` : ''}
                      </td>
                      <td className="text-end">
                        <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => openEdit(j)} data-testid={`jurisdiction-edit-${j.id}`}><i className="bi bi-pencil" /></Button>
                        <Button size="sm" variant="outline-danger" onClick={() => remove(j)} data-testid={`jurisdiction-delete-${j.id}`} disabled={j.type === 'state' || j.type === 'county'} title={j.type === 'state' || j.type === 'county' ? 'States and counties are managed via Locations' : ''}><i className="bi bi-trash" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          ))
        )}

        <Modal show={showForm} onHide={() => setShowForm(false)} centered>
          <Modal.Header closeButton><Modal.Title>{editing ? 'Edit jurisdiction' : 'New jurisdiction'}</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-2"><Form.Label>Name</Form.Label><Form.Control value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="jurisdiction-name-input" /></Form.Group>
            <Form.Group className="mb-2"><Form.Label>Slug</Form.Label><Form.Control value={form.slug} placeholder="auto-generated from name" onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} data-testid="jurisdiction-slug-input" /></Form.Group>
            <Form.Group className="mb-2"><Form.Label>Type</Form.Label>
              <Form.Select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} data-testid="jurisdiction-type-select">
                {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-2"><Form.Label>State (optional)</Form.Label>
              <Form.Select value={form.state_id} onChange={(e) => setForm((f) => ({ ...f, state_id: e.target.value }))} data-testid="jurisdiction-state-select">
                <option value="">— None —</option>
                {states.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button variant="primary" onClick={save} disabled={saving} data-testid="jurisdiction-save-button">{saving ? <Spinner size="sm" animation="border" /> : 'Save'}</Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
}
